import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import {
  equipmentApi,
  QueryEquipmentParams,
  CreateEquipmentDto,
  UpdateEquipmentDto,
  RecordDowntimeDto,
  EndDowntimeDto,
  UpdateMetersDto,
} from '../services/equipment.api';
import { toast } from 'sonner';
import { getErrorMessage } from '../client';

/**
 * Query hook for fetching paginated equipment list
 */
export function useEquipmentList(params: QueryEquipmentParams = {}) {
  return useQuery({
    queryKey: queryKeys.equipment.list(params),
    queryFn: () => equipmentApi.getAll(params),
  });
}

/**
 * Query hook for fetching a single equipment item
 */
export function useEquipment(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.equipment.detail(id || ''),
    queryFn: () => equipmentApi.getById(id!),
    enabled: !!id,
  });
}

/**
 * Query hook for fetching equipment with full hierarchy
 */
export function useEquipmentWithHierarchy(id: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.equipment.detail(id || ''), 'hierarchy'],
    queryFn: () => equipmentApi.getWithHierarchy(id!),
    enabled: !!id,
  });
}

/**
 * Query hook for fetching equipment tree
 */
export function useEquipmentTree() {
  return useQuery({
    queryKey: queryKeys.equipment.hierarchy(),
    queryFn: () => equipmentApi.getTree(),
  });
}

/**
 * Query hook for fetching equipment breadcrumbs
 */
export function useEquipmentBreadcrumbs(id: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.equipment.detail(id || ''), 'breadcrumbs'],
    queryFn: () => equipmentApi.getBreadcrumbs(id!),
    enabled: !!id,
  });
}

/**
 * Query hook for fetching equipment dashboard data
 */
export function useEquipmentDashboard() {
  return useQuery({
    queryKey: [...queryKeys.equipment.all, 'dashboard'],
    queryFn: () => equipmentApi.getDashboard(),
  });
}

/**
 * Query hook for fetching equipment analytics
 */
export function useEquipmentAnalytics() {
  return useQuery({
    queryKey: [...queryKeys.equipment.all, 'analytics'],
    queryFn: () => equipmentApi.getAnalytics(),
  });
}

/**
 * Query hook for fetching downtime history
 */
export function useDowntimeHistory(equipmentId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.equipment.detail(equipmentId || ''), 'downtime'],
    queryFn: () => equipmentApi.getDowntimeHistory(equipmentId!),
    enabled: !!equipmentId,
  });
}

/**
 * Mutation hook for creating equipment
 */
export function useCreateEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEquipmentDto) => equipmentApi.create(data),
    onSuccess: () => {
      toast.success('Equipment created successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.hierarchy() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for updating equipment
 */
export function useUpdateEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEquipmentDto }) =>
      equipmentApi.update(id, data),
    onSuccess: (_, variables) => {
      toast.success('Equipment updated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.hierarchy() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for deleting equipment
 */
export function useDeleteEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => equipmentApi.delete(id),
    onSuccess: () => {
      toast.success('Equipment deleted successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.hierarchy() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for recording downtime
 */
export function useRecordDowntime() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ equipmentId, data }: { equipmentId: string; data: RecordDowntimeDto }) =>
      equipmentApi.recordDowntime(equipmentId, data),
    onSuccess: (_, variables) => {
      toast.success('Downtime recorded successfully');
      queryClient.invalidateQueries({
        queryKey: queryKeys.equipment.detail(variables.equipmentId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for ending downtime
 */
export function useEndDowntime() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      equipmentId,
      eventId,
      data,
    }: {
      equipmentId: string;
      eventId: string;
      data: EndDowntimeDto;
    }) => equipmentApi.endDowntime(equipmentId, eventId, data),
    onSuccess: (_, variables) => {
      toast.success('Downtime ended successfully');
      queryClient.invalidateQueries({
        queryKey: queryKeys.equipment.detail(variables.equipmentId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for updating equipment meters/operating hours
 */
export function useUpdateMeters() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMetersDto }) =>
      equipmentApi.updateMeters(id, data),
    onSuccess: (_, variables) => {
      toast.success('Operating hours updated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.equipment.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
